import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { HelpCircle, ChevronDown, Compass, ShieldCheck, Star } from 'lucide-react';

interface FAQItem {
  question: string;
  answer: string;
  category: 'board' | 'membership';
}

const faqs: FAQItem[] = [
  {
    category: 'board',
    question: "What makes the OWS EXPEDITION CX2 different from standard touring paddleboards?",
    answer: "Ordinary boards are built for near-shore, calm-water recreational paddling, utilizing generic round nose shapes and single-chamber construction. The CX2 is engineered as a dedicated open-water expedition board. It features our Signature Dual-Chamber safety cell configuration for buoyant redundancy, a sharp displacement hull nose to slice cleanly through heavy boat wakes, a persistent high-visibility naval-white deck, and a wide, flat 32-inch deck profile that maximizes foot leverage against sudden side swell chop."
  },
  {
    category: 'board',
    question: "What is the purpose of the Dual-Chamber design on the CX2?",
    answer: "Our Dual-Chamber technology provides absolute survival redundancy for long-range marine crossings. The vessel's volume is split between two independently sealed and pressurized high-inflation cells. In the rare event that the outer rail suffers a physical puncture or collision with submerged debris, the secondary inner chamber maintains full pneumatic pressure, guaranteeing you remain safely afloat and high in the water until recovery is completed."
  },
  {
    category: 'board',
    question: "How does the displacement nose affect tracking and speed?",
    answer: "Traditional flat planing hulls bounce off surface chop, causing constant visual drift and requiring frequent corrective paddling. The CX2's sharp displacement nose acts like a traditional ship bow, piercing through turbulent waves, small-craft wakes, and head winds. This maintains excellent directional tracking and velocity, heavily reducing human fatigue profiles on multi-kilometer expeditions."
  },
  {
    category: 'membership',
    question: "What is the difference between a Prospect and an active Syndicate Member?",
    answer: "A Prospect is our complimentary, entry-level recruit tier. It grants immediate basic access to our scheduling calendar for training, single-day clinics, and standard rentals on a pay-as-you-go basis. An active Syndicate Member ($300 seasonal activation) unlocks full ecosystem integration, including complimentary members-only group trips, 50% private coaching discounts, expert-led safety workshops, and clearance to reserve advanced expeditions (such as Night Raids and deep basin crossings)."
  },
  {
    category: 'membership',
    question: "How is the prestigious Elite Status rank achieved?",
    answer: "Elite Status is a merit-only, unpurchasable milestone with deep cultural prestige in the squad. It is awarded exclusively to Syndicate Members who successfully plan, clear, and execute a sanctioned Lake Ontario crossing under OWS coiled safety protocols. Unlocking Elite Status grants legacy classification, access to exclusive hardware prototype testing, and advanced coiled convoy invitations."
  },
  {
    category: 'membership',
    question: "Can I manage or upgrade my membership tier at any time?",
    answer: "Absolutely. All membership status adjustments, upgrades, waivers, and progression tracking are managed directly within your Member Portal dashboard. Active Syndicate memberships run seasonally, allowing complete flexibility to upgrade or pause your status to match your physical training windows."
  }
];

export default function FAQ() {
  const [activeCategory, setActiveCategory] = useState<'all' | 'board' | 'membership'>('all');
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const filteredFaqs = faqs.filter(
    (faq) => activeCategory === 'all' || faq.category === activeCategory
  );

  const toggleOpen = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section 
      id="ows-faq-section" 
      className="py-24 bg-ows-white text-ows-dark overflow-hidden relative border-t border-[#E0E0E0]"
    >
      {/* Background visual graphics */}
      <div className="absolute top-1/2 left-0 w-96 h-96 bg-ows-teal/5 rounded-full -translate-y-1/2 -ml-48 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-ows-sand/20 rounded-full translate-y-1/3 translate-x-1/3 blur-3xl pointer-events-none" />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-2 text-ows-teal mb-4">
            <HelpCircle className="h-5 w-5" />
            <span className="text-[10px] font-mono font-bold uppercase tracking-[0.4em]">ows_intelligence</span>
          </div>
          <h2 id="faq-heading" className="text-3xl md:text-5xl font-display font-light uppercase tracking-tight italic-serif text-ows-dark">
            FREQUENTLY ASKED <br />
            <span className="text-ows-teal">QUESTIONS.</span>
          </h2>
          <p id="faq-subheading" className="text-sm text-ows-dark/60 mt-4 max-w-lg mx-auto font-mono uppercase tracking-tighter">
            Everything you need to know about the OWS EXPEDITION CX2 and our tiered squad architecture.
          </p>
        </div>

        {/* Filter Tabs */}
        <div id="faq-filter-container" className="flex justify-center gap-4 mb-12">
          {[
            { id: 'all', label: 'All Intel' },
            { id: 'board', label: 'CX2 Board', icon: Compass },
            { id: 'membership', label: 'Memberships', icon: ShieldCheck }
          ].map((cat) => {
            const Icon = cat.icon;
            const isActive = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                id={`faq-tab-${cat.id}`}
                onClick={() => {
                  setActiveCategory(cat.id as any);
                  setOpenIndex(null);
                }}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-[10px] font-mono font-bold uppercase tracking-widest border transition-all duration-300 ${
                  isActive
                    ? 'bg-ows-teal border-ows-teal text-white shadow-md'
                    : 'bg-white border-[#E0E0E0] text-ows-dark/60 hover:text-ows-dark hover:border-ows-teal/40'
                }`}
              >
                {Icon && <Icon className="h-3.5 w-3.5" />}
                {cat.label}
              </button>
            );
          })}
        </div>

        {/* Accordion List */}
        <div id="faq-accordion-container" className="space-y-4">
          <AnimatePresence mode="popLayout">
            {filteredFaqs.map((faq, index) => {
              const isOpen = openIndex === index;
              return (
                <motion.div
                  key={faq.question}
                  layout
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  transition={{ duration: 0.3 }}
                  className={`border rounded-2xl transition-all duration-300 overflow-hidden bg-white ${
                    isOpen 
                      ? 'border-ows-teal shadow-lg' 
                      : 'border-[#E0E0E0] hover:border-ows-teal/40'
                  }`}
                >
                  <button
                    id={`faq-btn-${index}`}
                    onClick={() => toggleOpen(index)}
                    aria-expanded={isOpen}
                    className="w-full text-left p-6 md:p-8 flex justify-between items-center gap-4 focus:outline-none"
                  >
                    <div className="flex items-center gap-3">
                      {faq.category === 'board' ? (
                        <Compass className={`h-4.5 w-4.5 shrink-0 transition-colors ${isOpen ? 'text-ows-teal' : 'text-ows-dark/40'}`} />
                      ) : (
                        <ShieldCheck className={`h-4.5 w-4.5 shrink-0 transition-colors ${isOpen ? 'text-ows-teal' : 'text-ows-dark/40'}`} />
                      )}
                      <span className="font-sans font-medium text-xs md:text-sm uppercase tracking-wider text-ows-dark">
                        {faq.question}
                      </span>
                    </div>
                    <motion.div
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronDown className={`h-5 w-5 shrink-0 transition-colors ${isOpen ? 'text-ows-teal' : 'text-ows-dark/40'}`} />
                    </motion.div>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: "auto" }}
                        exit={{ height: 0 }}
                        transition={{ duration: 0.25, ease: "easeInOut" }}
                      >
                        <div className="px-6 pb-6 md:px-8 md:pb-8 border-t border-dashed border-[#E0E0E0]/60 pt-4">
                          <p id={`faq-ans-${index}`} className="text-xs md:text-[13px] leading-relaxed text-[#141414]/70 font-light font-sans tracking-wide">
                            {faq.answer}
                          </p>
                          <div className="flex items-center gap-1.5 mt-4 text-[9px] font-mono uppercase tracking-widest text-ows-teal/65">
                            <span className="h-1.5 w-1.5 rounded-full bg-ows-teal animate-pulse" />
                            verified intel // classification: {faq.category}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
